import type {
    RefObject
} from 'react';
import {
    useCallback,
    useMemo
} from 'react';
import type {
    Settings
} from 'react-slick';

type UseSlickAriaHiddenFixOptions = {
    focusableSelector?: string;
};

const useSlickAriaHiddenFix = (
    carouselRef: RefObject<HTMLElement | null>,
    baseSettings: Settings,
    options: UseSlickAriaHiddenFixOptions = {}
): Settings => {
    const {
        focusableSelector = 'button'
    } = options;

    const updateSlideTabIndex = useCallback(
        () => {
            const root = carouselRef.current;

            if (!root) { return; }

            const slides = root.querySelectorAll('.slick-slide');
            const focusedElement = document.activeElement;
            const focusIsBeingHidden = [
                ...slides
            ].some((slide) => (
                slide.getAttribute('aria-hidden') === 'true' && slide.contains(focusedElement)
            ));

            slides.forEach((slide) => {
                const isHidden = slide.getAttribute('aria-hidden') === 'true';

                const focusables = slide.querySelectorAll<HTMLElement>(focusableSelector);

                focusables.forEach((element) => {
                    const el = element;

                    el.tabIndex = isHidden ? -1 : 0;
                });
            });

            const arrows = root.querySelectorAll<HTMLElement>('.slick-arrow');

            arrows.forEach((arrow) => {
                arrow.setAttribute('aria-disabled', String(arrow.classList.contains('slick-disabled')));
            });
            const arrowIsBeingHidden = focusedElement instanceof Element
                && focusedElement.matches('.slick-arrow.slick-disabled');

            if (focusIsBeingHidden || arrowIsBeingHidden) {
                const visibleSlide = [
                    ...slides
                ].find((slide) => slide.getAttribute('aria-hidden') !== 'true');

                visibleSlide?.querySelector<HTMLElement>(focusableSelector)?.focus({
                    preventScroll: true
                });
            }
        },
        [
            carouselRef,
            focusableSelector
        ]
    );

    const mergedSettings: Settings = useMemo(
        () => {
            const {
                afterChange,
                onInit,
                onReInit,
                ...rest
            } = baseSettings;

            return {
                ...rest,

                afterChange: (...args) => {
                    if (afterChange) {
                        afterChange(...args);
                    }
                    updateSlideTabIndex();
                },

                onInit: (...args) => {
                    if (onInit) {
                        onInit(...args);
                    }
                    updateSlideTabIndex();
                },

                onReInit: (...args) => {
                    if (onReInit) {
                        onReInit(...args);
                    }
                    updateSlideTabIndex();
                }
            };
        },
        [
            baseSettings,
            updateSlideTabIndex
        ]
    );

    return mergedSettings;
};

export default useSlickAriaHiddenFix;
